import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { hasSalonPermission as hasSalonPermissionUtil, resolveSalonPermissions } from '../../config/salonPermissions';
import { API_URL, SALON_API_URL } from '../../api/config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const [isSalonAdmin, setIsSalonAdmin] = useState(false);
  const [salonPermissions, setSalonPermissions] = useState([]);
  const [restrictedFeatures, setRestrictedFeatures] = useState([]);
  const [managerFullAccess, setManagerFullAccess] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');

  useEffect(() => {
    let isMounted = true;

    const loadStoredAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUserData = localStorage.getItem('userData');
        const storedRole = localStorage.getItem('userRole');
        const storedSalonAdmin = localStorage.getItem('isSalonAdmin') === 'true';
        const storedSalonPermissions = localStorage.getItem('salonPermissions');

        if (isMounted) {
          if (token && storedUserData) {
            setUserToken(token);
            try {
              const parsedUser = JSON.parse(storedUserData);
              setUserData(parsedUser);
              setRestrictedFeatures(parsedUser.restrictedFeatures || []);
              setManagerFullAccess(parsedUser.managerFullAccess !== false);
              setLanguage(parsedUser.language || 'en');
              localStorage.setItem('language', parsedUser.language || 'en');
            } catch (err) {
              console.error('Error parsing stored user data:', err);
              localStorage.removeItem('userData');
            }
          }

          setUserRole(storedRole || 'admin');
          setIsSalonAdmin(storedSalonAdmin);
          try {
            const parsed = storedSalonPermissions ? JSON.parse(storedSalonPermissions) : [];
            setSalonPermissions(Array.isArray(parsed) ? parsed : []);
          } catch {
            setSalonPermissions([]);
          }
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadStoredAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const signIn = async ({ token, user, role = 'admin', isAdminSession = false }) => {
    try {
      if (typeof token !== 'string') {
        throw new Error('Token must be a string');
      }
      if (!user || typeof user !== 'object') {
        throw new Error('Invalid user data');
      }

      const effectiveRole = user.role || role;
      
      const isSalon = !!(user.salonId || user.salonName || !user.restaurantId);
      localStorage.setItem('businessType', isSalon ? 'salon' : 'restaurant');
      
      // Fetch restricted features for manager on Web
      let fetchRestricted = [];
      let fetchFullAccess = true;
      if (effectiveRole === 'manager') {
        try {
          const isSalon = !!user.salonId;
          const apiUrl = isSalon ? SALON_API_URL : API_URL;
          const endpoint = isSalon ? '/admin-settings' : '/pin-settings';
          const res = await fetch(apiUrl + endpoint, {
            headers: { Authorization: 'Bearer ' + token },
          });
          const data = await res.json();
          if (data.success && data.data) {
            fetchFullAccess = data.data.managerFullAccess !== false;
            fetchRestricted = data.data.restrictedFeatures || [];
          }
        } catch (err) {
          console.error('Error fetching manager permissions at sign-in:', err);
        }
      }

      const userWithRestrictions = {
        ...user,
        role: effectiveRole,
        restrictedFeatures: fetchRestricted,
        managerFullAccess: fetchFullAccess,
      };

      const effectivePermissions = resolveSalonPermissions(userWithRestrictions);
      const effectiveSalonAdmin = Boolean(isAdminSession || user.isOwner || effectiveRole === 'owner');

      const fullUser = {
        ...userWithRestrictions,
        permissions: effectivePermissions,
        isOwner: !!user.isOwner,
      };

      localStorage.setItem('token', token);
      localStorage.setItem('userData', JSON.stringify(fullUser));
      localStorage.setItem('userRole', effectiveRole);
      localStorage.setItem('isSalonAdmin', effectiveSalonAdmin ? 'true' : 'false');
      localStorage.setItem('salonPermissions', JSON.stringify(effectivePermissions));
      localStorage.setItem('language', fullUser.language || 'en');

      setUserToken(token);
      setUserData(fullUser);
      setUserRole(effectiveRole);
      setIsSalonAdmin(effectiveSalonAdmin);
      setSalonPermissions(effectivePermissions);
      setRestrictedFeatures(fetchRestricted);
      setManagerFullAccess(fetchFullAccess);
      setLanguage(fullUser.language || 'en');
      
      if (user.email && user.restaurantId) {
        const account = {
          email: user.email,
          restaurantId: user.restaurantId,
          restaurantName: user.restaurantName || '',
          outletName: user.outletName || ''
        };
        
        let savedAccounts = [];
        try {
          const saved = localStorage.getItem('savedAccounts');
          savedAccounts = saved ? JSON.parse(saved) : [];
          if (!Array.isArray(savedAccounts)) savedAccounts = [];
        } catch (e) {
          console.error('Error parsing saved accounts:', e);
          savedAccounts = [];
        }
        
        const existingIndex = savedAccounts.findIndex(
          a => a.email === user.email && a.restaurantId === user.restaurantId
        );
        
        if (existingIndex === -1) {
          savedAccounts.push(account);
        } else {
          savedAccounts[existingIndex] = account;
        }
        
        localStorage.setItem('savedAccounts', JSON.stringify(savedAccounts));
      }
      
      console.log('Auth state updated:', { token: !!token, user });
    } catch (error) {
      console.error('Error storing auth state:', error);
      throw new Error(`Failed to sign in: ${error.message}`);
    }
  };

  const signOut = async () => {
    try {
      const currentUser = userData;
      
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('userRole');
      localStorage.removeItem('isSalonAdmin');
      localStorage.removeItem('salonPermissions');
      localStorage.removeItem('businessType');
      
      // Clear active PIN verification sessions
      sessionStorage.clear();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('salon_report_access_')) {
          localStorage.removeItem(key);
        }
      });

      if (currentUser && currentUser.restaurantId && currentUser.email) {
        try {
          let savedAccounts = [];
          const saved = localStorage.getItem('savedAccounts');
          
          if (saved) {
            savedAccounts = JSON.parse(saved);
            if (!Array.isArray(savedAccounts)) savedAccounts = [];
          }
          
          const filteredAccounts = savedAccounts.filter(
            account => !(
              account.email === currentUser.email && 
              account.restaurantId === currentUser.restaurantId
            )
          );
          
          localStorage.setItem('savedAccounts', JSON.stringify(filteredAccounts));
        } catch (error) {
          console.error('Error removing saved account:', error);
        }
      }

      setUserToken(null);
      setUserData(null);
      setUserRole('admin');
      setIsSalonAdmin(false);
      setSalonPermissions([]);
      setRestrictedFeatures([]);
      setManagerFullAccess(true);
      
      console.log('✅ Sign out completed');
    } catch (error) {
      console.error('Error during sign out:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('userRole');
      localStorage.removeItem('isSalonAdmin');
      localStorage.removeItem('salonPermissions');
      localStorage.removeItem('businessType');
      sessionStorage.clear();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('salon_report_access_')) {
          localStorage.removeItem(key);
        }
      });
      setUserToken(null);
      setUserData(null);
      setUserRole('admin');
      setIsSalonAdmin(false);
      setSalonPermissions([]);
      setRestrictedFeatures([]);
      setManagerFullAccess(true);
      throw new Error('Failed to sign out');
    }
  };

  const updateUserData = async (newData) => {
    try {
      if (!userData) {
        throw new Error('No user data to update');
      }

      const updatedData = { ...userData, ...newData };
      const userString = JSON.stringify(updatedData);
      const nextPermissions = resolveSalonPermissions(updatedData);

      localStorage.setItem('userData', userString);
      localStorage.setItem('salonPermissions', JSON.stringify(nextPermissions));
      setUserData(updatedData);
      setSalonPermissions(nextPermissions);
      if (newData.restrictedFeatures !== undefined) {
        setRestrictedFeatures(newData.restrictedFeatures);
      }
      if (newData.managerFullAccess !== undefined) {
        setManagerFullAccess(newData.managerFullAccess);
      }
    } catch (error) {
      console.error('Error updating user data:', error);
      throw new Error('Failed to update user data');
    }
  };

  const updateUserRole = (role) => {
    try {
      localStorage.setItem('userRole', role);
      setUserRole(role);
    } catch (error) {
      console.error('Error updating user role:', error);
      throw new Error('Failed to update user role');
    }
  };

  const hasSalonPermission = (permission) => {
    if (!permission) return false;
    return hasSalonPermissionUtil(salonPermissions, permission);
  };

  const isOutletSaved = (restaurantId, email) => {
    try {
      const saved = localStorage.getItem('savedAccounts');
      if (!saved) return false;
      
      const accounts = JSON.parse(saved);
      if (!Array.isArray(accounts)) return false;
      
      return accounts.some(
        account => account.restaurantId === restaurantId && account.email === email
      );
    } catch (error) {
      console.error('Error checking saved outlet:', error);
      return false;
    }
  };

  const removeSavedAccount = (restaurantId, email) => {
    try {
      let savedAccounts = [];
      const saved = localStorage.getItem('savedAccounts');
      
      if (saved) {
        savedAccounts = JSON.parse(saved);
        if (!Array.isArray(savedAccounts)) savedAccounts = [];
      }
      
      const filteredAccounts = savedAccounts.filter(
        account => !(account.restaurantId === restaurantId && account.email === email)
      );
      
      localStorage.setItem('savedAccounts', JSON.stringify(filteredAccounts));
      
      console.log('Removed saved account:', { restaurantId, email });
    } catch (error) {
      console.error('Error removing saved account:', error);
    }
  };
  const updateUserLanguage = async (lang) => {
    try {
      if (!userToken) return false;
      const isSalon = localStorage.getItem('businessType') === 'salon';
      const url = isSalon ? `${SALON_API_URL}/salon-users/me/language` : `${API_URL}/auth/me/language`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ language: lang })
      });

      if (response.ok) {
        localStorage.setItem('language', lang);
        setLanguage(lang);
        setUserData(prev => {
          if (!prev) return null;
          const updated = { ...prev, language: lang };
          localStorage.setItem('userData', JSON.stringify(updated));
          return updated;
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating language:', err);
      return false;
    }
  };

  const signOutRef = useRef(signOut);
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (response.status === 401) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
          const isAuthRequest = url.includes('/auth/');
          if (!isAuthRequest) {
            console.warn('Unauthorized (401) request detected, signing out...');
            
            // Determine redirect path before clearing auth state
            const isSalon = localStorage.getItem('businessType') === 'salon';
            const redirectUrl = isSalon ? '/salon/signin' : '/signin';
            
            signOutRef.current();
            
            window.location.href = redirectUrl;
            return new Promise(() => {}); // hang the promise to prevent subsequent code execution and alert popups
          }
        }
        return response;
      } catch (error) {
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated: !!userToken,
        userToken,
        userData,
        user: userData,
        userRole,
        isSalonAdmin,
        salonPermissions,
        hasSalonPermission,
        restrictedFeatures,
        managerFullAccess,
        restaurantId: userData?.restaurantId,
        signIn,
        signOut,
        updateUserData,
        updateUserRole,
        isOutletSaved,
        removeSavedAccount,
        language,
        updateUserLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;